import type { SocialItemEnum, SocialItemServer } from '@metapic-apps/shared'

import { convertMinorAmountToMajor } from '@/legacy/store/helpFunctions'
import { normalizeMoney } from '@/lib/adapters/common.adapter'
import { normalizeImage } from '@/lib/adapters/image.adapters'
import type {
  CreatorItem,
  CreatorItemFilter,
  CreatorItemRaw,
  UserStatsParams,
} from '@/lib/types/creator.types'

const parseRoas = (roas: CreatorItemRaw['roas']): number | null => {
  if (roas === undefined || roas === null || roas === 'inf') {
    return null
  }
  const value = Number(roas)
  return Number.isFinite(value) ? value : null
}

const buildFollowersByPlatform = (
  socialMedia: SocialItemServer[] | undefined,
): Partial<Record<SocialItemEnum, number>> =>
  (socialMedia ?? []).reduce<Partial<Record<SocialItemEnum, number>>>((acc, item) => {
    if (item.followers != null) {
      acc[item.type] = (acc[item.type] ?? 0) + item.followers
    }
    return acc
  }, {})

export const normalizeCreator = (raw: CreatorItemRaw): CreatorItem => {
  const followersByPlatform = buildFollowersByPlatform(raw.social_media)
  const totalFollowers = Object.values(followersByPlatform).reduce((sum, value) => sum + value, 0)

  return {
    userId: raw.user_id,
    username: raw.username,
    tags: (raw.user_tags ?? []).map((tag) => ({ id: tag.id, name: tag.name })),
    totalFollowers,
    followersByPlatform,
    clicks: Number(raw.clicks),
    cost: convertMinorAmountToMajor(raw.store_cost),
    orderValue: raw.order_value !== undefined ? convertMinorAmountToMajor(raw.order_value) : 0,
    nrOfOrders: raw.nr_orders !== undefined ? Number(raw.nr_orders) : 0,
    roas: parseRoas(raw.roas),
    mediaValue: normalizeMoney(raw.media_value),
    ...(raw.user_image && { avatar: normalizeImage(raw.user_image) }),
  }
}

export const normalizeCreators = (rawList: CreatorItemRaw[]): CreatorItem[] =>
  rawList.map(normalizeCreator)

export const handleCreatorsFilter = (filter: CreatorItemFilter): UserStatsParams => ({
  ...(filter.dateFrom && filter.dateTo && { from: filter.dateFrom, to: filter.dateTo }),
  orderBy: filter.sortBy,
  orderByOrder: filter.sortOrder,
  ...(filter.query && { query: filter.query }),
  ...(filter.trafficSource && { traffic_source_code: filter.trafficSource }),
  ...(filter.tags?.length && { user_tags: filter.tags }),
  ...(filter.segmentId && { segment_ids: [filter.segmentId] }),
  page: filter.page,
})
